import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Add, DownloadOutlined } from '@mui/icons-material';
import { useRef, useState } from 'react';
import { AttachmentItem, FormDialog, StatusBadge } from '../../design-system';
import { useAsyncResource } from '../../shared/useAsyncResource';
import { AbsenceEvidenceField, type AbsenceEvidencePort, type AbsenceEvidenceSelection } from './AbsenceEvidenceField';
import { localToday } from './absenceCalendarModel';
import { absenceEvidenceStatus } from './absenceEvidenceStatus';

export type AbsenceLinkedEvidence = { id: string; displayName: string; scanStatus: string };
export type AbsenceRequestEvidencePort = {
  list(requestId: string): Promise<AbsenceLinkedEvidence[]>;
  add(requestId: string, evidenceDocuments: string[], idempotencyKey: string): Promise<AbsenceLinkedEvidence[]>;
  download(documentId: string): Promise<Blob>;
};
export type AbsenceEvidenceReadPort = Pick<AbsenceRequestEvidencePort, 'list' | 'download'>;

type EvidenceRequirement = { policySnapshot?: string; requestStatus?: string; endDate?: string };

export function AbsenceLinkedEvidenceList({ requestId, port, fixtureMode = false, revision = 0, requirement }: {
  requestId: string;
  port: AbsenceEvidenceReadPort;
  fixtureMode?: boolean;
  revision?: number;
  requirement?: EvidenceRequirement;
}) {
  const documents = useAsyncResource(() => fixtureMode ? Promise.resolve<AbsenceLinkedEvidence[]>([]) : port.list(requestId), [port, requestId, fixtureMode, revision]);
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState('');
  const evidenceStatus = documents.data && !documents.loading && !documents.error
    ? absenceEvidenceStatus({ ...requirement, today: localToday(), documents: documents.data }) : undefined;
  const download = async (item: AbsenceLinkedEvidence) => {
    setDownloading(item.id); setDownloadError('');
    try {
      const blob = await port.download(item.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = item.displayName; anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { setDownloadError(error instanceof Error ? error.message : '증빙을 열지 못했습니다. 문서 조회 권한과 검사 상태를 확인해 주세요.'); }
    finally { setDownloading(''); }
  };
  return <Stack spacing={1}>
    {evidenceStatus && <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><StatusBadge {...evidenceStatus} /><Typography variant="caption" color="text.secondary">신청 당시 기준</Typography></Stack>}
    {documents.loading ? <Typography variant="body2" role="status">증빙을 불러오는 중입니다.</Typography>
      : documents.error ? <Alert severity="error" action={<Button color="inherit" onClick={documents.reload}>다시 불러오기</Button>}>증빙을 확인하지 못했습니다. 조회 권한을 확인하거나 다시 시도해 주세요.</Alert>
        : documents.data?.length ? documents.data.map((item) => <AttachmentItem key={item.id} name={item.displayName}
          description={item.scanStatus === 'CLEAN' ? '보안 검사 완료' : '보안 검사 미완료 · 다운로드 불가'}
          actions={<Button variant="outlined" size="small" startIcon={<DownloadOutlined />} disabled={item.scanStatus !== 'CLEAN' || Boolean(downloading)} onClick={() => { void download(item); }}>{downloading === item.id ? '여는 중' : '다운로드'}</Button>} />)
          : <Typography variant="body2" color="text.secondary">제출한 증빙이 없습니다.</Typography>}
    {downloadError && <Alert severity="error">{downloadError}</Alert>}
  </Stack>;
}

export function AbsenceRequestEvidenceSection({ requestId, workerId, personId, port, uploadPort, canWrite, canReadDocuments, canUploadDocuments, fixtureMode = false, onSaved, requirement }: {
  requestId: string;
  workerId: string;
  personId?: string;
  port: AbsenceRequestEvidencePort;
  uploadPort?: AbsenceEvidencePort;
  canWrite: boolean;
  canReadDocuments: boolean;
  canUploadDocuments: boolean;
  fixtureMode?: boolean;
  onSaved?: () => void;
  requirement?: EvidenceRequirement;
}) {
  const [open, setOpen] = useState(false);
  const [openRevision, setOpenRevision] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [selectionState, setSelectionState] = useState<AbsenceEvidenceSelection>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const attempt = useRef<{ signature: string; key: string } | null>(null);
  const selectionReady = selectionState?.workerId === workerId
    && JSON.stringify(selectionState.documentIds) === JSON.stringify(selected) && selectionState.ready;
  const submitDisabled = !canWrite || !canReadDocuments || !selected.length || pending || !selectionReady;
  const submit = async () => {
    if (busy || submitDisabled) return;
    const signature = JSON.stringify([requestId, [...selected].sort()]);
    if (attempt.current?.signature !== signature) attempt.current = { signature, key: crypto.randomUUID() };
    setBusy(true); setError('');
    try {
      if (!fixtureMode) await port.add(requestId, selected, attempt.current.key);
      attempt.current = null; setOpen(false); setSelected([]); setRevision((value) => value + 1); onSaved?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : '증빙을 제출하지 못했습니다. 다시 시도해 주세요.'); }
    finally { setBusy(false); }
  };
  return <Box component="section" aria-label="휴가 증빙">
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}><Typography component="h3" variant="subtitle2">증빙 문서</Typography>
      {canWrite && canReadDocuments && <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { setError(''); setOpenRevision((value) => value + 1); setOpen(true); }}>증빙 추가</Button>}
    </Stack>
    {canReadDocuments ? <AbsenceLinkedEvidenceList port={port} requestId={requestId} fixtureMode={fixtureMode} revision={revision} requirement={requirement} />
      : <Typography variant="body2" color="text.secondary">증빙을 확인하려면 문서 조회 권한이 필요합니다.</Typography>}
    <FormDialog open={open} keepMounted={openRevision > 0} title="휴가 증빙 제출" onClose={() => { if (!busy) setOpen(false); }} onSubmit={() => { void submit(); }}
      submitLabel="증빙 제출" cancelLabel="닫기" submitDisabled={submitDisabled} busy={busy}>
      <Stack spacing={2}>{error && <Alert severity="error">{error}</Alert>}
        {pending && <Alert severity="info">첨부 중인 문서의 업로드·보안 검사를 기다리거나 첨부를 취소해 주세요.</Alert>}
        {openRevision > 0 && <AbsenceEvidenceField port={uploadPort} workerId={workerId} personId={personId} value={selected} onChange={setSelected} onPendingChange={setPending} onSelectionStateChange={setSelectionState} refreshKey={openRevision}
          canRead={canReadDocuments} canUpload={canUploadDocuments} disabled={busy} fixtureMode={fixtureMode} />}
      </Stack>
    </FormDialog>
  </Box>;
}
